
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MultiSelect } from '@/components/ui/multi-select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Brain } from 'lucide-react';
import { formatDateTime, formatJobType, getEvaluationRoundLabel } from '@/utils/formatters';
import { JOB_TYPES, EVALUATION_ROUNDS } from '@/types/enums';
import type { Tables } from '@/integrations/supabase/types';

type AIQuestionTemplate = Tables<'ai_question_templates'>;

const templateSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  prompt_template: z.string().min(1, 'Prompt template is required'),
  question_type: z.string().min(1, 'Question type is required'),
  job_role_ids: z.array(z.string()).optional().default([]),
  job_role_types: z
    .array(z.string())
    .refine((arr) => arr.every((v) => Object.values(JOB_TYPES).includes(v as any)), {
      message: 'Invalid job type',
    })
    .optional()
    .default([]),
  rounds: z
    .array(z.string())
    .refine((arr) => arr.every((v) => Object.values(EVALUATION_ROUNDS).includes(v as any)), {
      message: 'Invalid round',
    })
    .optional()
    .default([]),
  is_active: z.boolean().default(true),
});

type TemplateForm = z.infer<typeof templateSchema>;

export default function AIQuestionTemplatesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<AIQuestionTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: jobPositions = [] } = useQuery({
    queryKey: ['job-positions', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('job_positions')
        .select('id, role_name, job_type')
        .eq('is_active', true)
        .order('role_name');
      if (error) throw error;
      return data || [];
    },
  });
const jobPositionMap = jobPositions.reduce<Record<string, string>>((acc, p: any) => {
    acc[p.id] = p.role_name;
    return acc;
  }, {});
  const jobPositionOptions = jobPositions.map((p: any) => ({
    value: p.id,
    label: `${p.role_name} (${formatJobType(p.job_type)})`,
  }));
  const jobTypeOptions = Object.values(JOB_TYPES).map((t) => ({
    value: t,
    label: formatJobType(t),
  }));
  const roundOptions = Object.values(EVALUATION_ROUNDS).map((r) => ({
    value: r,
    label: getEvaluationRoundLabel(r),
  }));

  const form = useForm<TemplateForm>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      description: '',
      prompt_template: '',
      question_type: '',
      job_role_ids: [],
      job_role_types: [],
      rounds: [],
      is_active: true,
    },
  });

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['ai-question-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_question_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: async (data: TemplateForm) => {
      // Ensure all required fields are present
      const insertData = {
        name: data.name,
        description: data.description || null,
        prompt_template: data.prompt_template,
        question_type: data.question_type,
        job_role_ids: data.job_role_ids,
        job_role_types: data.job_role_types,
        rounds: data.rounds,
        is_active: data.is_active,
      };

      const { error } = await supabase
        .from('ai_question_templates')
        .insert(insertData);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-question-templates'] });
      toast({
        title: 'Success',
        description: 'Template created successfully.',
      });
      setDialogOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: TemplateForm }) => {
      // Ensure all required fields are present
      const updateData = {
        name: data.name,
        description: data.description || null,
        prompt_template: data.prompt_template,
        question_type: data.question_type,
        job_role_ids: data.job_role_ids,
        job_role_types: data.job_role_types,
        rounds: data.rounds,
        is_active: data.is_active,
      };

      const { error } = await supabase
        .from('ai_question_templates')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-question-templates'] });
      toast({
        title: 'Success',
        description: 'Template updated successfully.',
      });
      setDialogOpen(false);
      setEditingTemplate(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('ai_question_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-question-templates'] });
      toast({
        title: 'Success',
        description: 'Template deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: TemplateForm) => {
    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createTemplateMutation.mutate(data);
    }
  };

  const handleEdit = (template: AIQuestionTemplate) => {
    setEditingTemplate(template);
    form.reset({
      name: template.name,
      description: template.description || '',
      prompt_template: template.prompt_template,
      question_type: template.question_type,
      job_role_ids: template.job_role_ids || [],
      job_role_types: template.job_role_types || [],
      rounds: template.rounds || [],
      is_active: template.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      deleteTemplateMutation.mutate(id);
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    form.reset({
      name: '',
      description: '',
      prompt_template: '',
      question_type: '',
      job_role_ids: [],
      job_role_types: [],
      rounds: [],
      is_active: true,
    });
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">Loading AI question templates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">AI Question Templates</h1>
          <p className="text-muted-foreground">Manage AI question generation templates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Template name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Input placeholder="Template description" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="question_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Question Type</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., technical, behavioral, problem_solving" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="job_role_ids"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Positions</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={jobPositionOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select job positions"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="job_role_types"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Types</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={jobTypeOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select job types"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rounds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Evaluation Rounds</FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={roundOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select rounds"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="prompt_template"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prompt Template</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Enter the AI prompt template. Use {skills}, {position}, etc. as placeholders."
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Active</FormLabel>
                        <div className="text-sm text-muted-foreground">
                          Enable this template for AI question generation
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}>
                    {editingTemplate ? 'Update' : 'Create'} Template
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            AI Question Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No templates found. Create your first template to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  {/* <TableHead>Description</TableHead> */}
                  <TableHead>Job Positions</TableHead>
                  <TableHead>Job Types</TableHead>
                  <TableHead>Rounds</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{template.question_type}</Badge>
                    </TableCell>
                    {/* <TableCell className="max-w-xs truncate">
                      {template.description || 'No description'}
                    </TableCell> */}
                    <TableCell className="max-w-xs truncate">
                      {template.job_role_ids && template.job_role_ids.length > 0
                        ? template.job_role_ids
                            .map((id) => jobPositionMap[id] || id)
                            .join(', ')
                        : 'All'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {template.job_role_types && template.job_role_types.length > 0
                        ? template.job_role_types.map((t) => formatJobType(t)).join(', ')
                        : 'All'}
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {template.rounds && template.rounds.length > 0
                        ? template.rounds.map((r) => getEvaluationRoundLabel(r)).join(', ')
                        : 'All'}
                    </TableCell>
                    
                    <TableCell>
                      <Badge variant={template.is_active ? 'default' : 'secondary'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(template.id)}
                          disabled={deleteTemplateMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
